import { useEffect, useState } from "preact/hooks";
import type { LoginProps } from "../../sdk/theme";
import { ApiFailure, api } from "../api";
import { Icon } from "../components/Icon";
import { Logo } from "../components/Logo";
import { Field, Toggle } from "../components/ui";
import { overridable } from "../overrides";
import {
  autofillPasskeys,
  autofillSupported,
  passkeysSupported,
  signInWithPasskey,
  wasCancelled,
} from "../passkeys";

const DefaultLogin = ({ appName, onDone }: LoginProps) => {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [needsCode, setNeedsCode] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [autofill, setAutofill] = useState<AbortController | null>(null);

  // Offer saved passkeys in the email field's autofill where the browser supports it.
  useEffect(() => {
    const controller = new AbortController();
    void autofillSupported().then((supported) => {
      if (!supported || controller.signal.aborted) return;
      setAutofill(controller);
      void autofillPasskeys(controller.signal, setError).then((signedIn) => {
        if (signedIn) onDone();
      });
    });
    return () => controller.abort();
  }, []);

  const usePasskey = async () => {
    autofill?.abort();
    setBusy(true);
    setError("");
    try {
      await signInWithPasskey({});
      onDone();
    } catch (failure) {
      if (!wasCancelled(failure))
        setError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (event: Event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/login", { login, password, ...(needsCode ? { code } : {}) });
      onDone();
    } catch (failure) {
      if (failure instanceof ApiFailure && failure.code === "totp_required") {
        setNeedsCode(true);
      } else {
        setError(failure instanceof Error ? failure.message : String(failure));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="auth-page" data-part="login">
      <form class="auth-card" data-part="login-card" onSubmit={submit}>
        <div class="brand">
          <Logo appName={appName} size={34} />
          <h1>Sign in</h1>
          <p>Your mail, on your own Cloudflare.</p>
        </div>
        {!needsCode ? (
          <>
            <Field label="Email address">
              <input
                type="email"
                autocomplete="username webauthn"
                required
                autofocus
                value={login}
                onInput={(event) => setLogin((event.target as HTMLInputElement).value)}
              />
            </Field>
            <Field label="Password">
              <input
                type="password"
                autocomplete="current-password"
                required
                value={password}
                onInput={(event) => setPassword((event.target as HTMLInputElement).value)}
              />
            </Field>
          </>
        ) : (
          <Field
            label="Authenticator code"
            hint="Open your authenticator app and enter the 6-digit code."
          >
            <input
              inputMode="numeric"
              autocomplete="one-time-code"
              pattern="[0-9 ]{6,7}"
              required
              autofocus
              value={code}
              onInput={(event) => setCode((event.target as HTMLInputElement).value)}
            />
          </Field>
        )}
        {error && <p class="form-error">{error}</p>}
        <button type="submit" class="primary wide" disabled={busy}>
          {busy ? "Signing in…" : needsCode ? "Verify" : "Sign in"}
        </button>
        {passkeysSupported() && (
          <>
            <div class="auth-divider">
              <span>or</span>
            </div>
            <button type="button" class="wide" disabled={busy} onClick={usePasskey}>
              <Icon name="key" size={16} />{" "}
              {needsCode ? "Use a passkey instead" : "Sign in with a passkey"}
            </button>
          </>
        )}
      </form>
    </div>
  );
};

export const Login = overridable("Login", DefaultLogin);

export const Setup = ({
  appName,
  tokenConfigured,
  onDone,
}: {
  appName: string;
  tokenConfigured: boolean;
  onDone: () => void;
}) => {
  const [form, setForm] = useState({
    token: "",
    login: "",
    name: "",
    password: "",
    confirm: "",
    catchAll: false,
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (key: keyof typeof form) => (event: Event) =>
    setForm({ ...form, [key]: (event.target as HTMLInputElement).value });

  const submit = async (event: Event) => {
    event.preventDefault();
    if (form.password !== form.confirm) {
      setError("The two passwords do not match.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/setup", {
        token: form.token,
        login: form.login,
        name: form.name,
        password: form.password,
        catchAll: form.catchAll,
      });
      onDone();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="auth-page" data-part="setup">
      <form class="auth-card" onSubmit={submit}>
        <div class="brand">
          <Logo appName={appName} size={34} />
          <h1>Set up {appName}</h1>
        </div>
        <p class="muted">
          Create the administrator mailbox. Its domain becomes the first domain {appName} receives
          mail for.
        </p>
        {!tokenConfigured && (
          <p class="form-error">
            Set a setup token first: run <code>wrangler secret put SETUP_TOKEN</code>, then reload
            this page.
          </p>
        )}
        <Field label="Setup token" hint="The SETUP_TOKEN secret you set with wrangler.">
          <input type="password" required value={form.token} onInput={set("token")} />
        </Field>
        <Field label="Your email address">
          <input
            type="email"
            required
            placeholder="you@yourdomain.com"
            value={form.login}
            onInput={set("login")}
          />
        </Field>
        <Field label="Your name">
          <input value={form.name} onInput={set("name")} />
        </Field>
        <Field label="Password" hint="At least 10 characters.">
          <input
            type="password"
            autocomplete="new-password"
            required
            minLength={10}
            value={form.password}
            onInput={set("password")}
          />
        </Field>
        <Field label="Confirm password">
          <input
            type="password"
            autocomplete="new-password"
            required
            value={form.confirm}
            onInput={set("confirm")}
          />
        </Field>
        <Toggle
          label="Catch-all"
          hint="Also deliver mail for unknown addresses on this domain to you."
          checked={form.catchAll}
          onChange={(catchAll) => setForm({ ...form, catchAll })}
        />
        {error && <p class="form-error">{error}</p>}
        <button type="submit" class="primary wide" disabled={busy || !tokenConfigured}>
          {busy ? "Creating…" : "Create mailbox"}
        </button>
      </form>
    </div>
  );
};
